<template>
  <communication-profile-numbers
    :placement="placement"
    trigger="click"
    close-on-auto-selection
  >
    <template #label="{ numbersTooltipVisible }">
      <div class="group flex items-center gap-x-2">
        <lf-pill
          size="sm"
          class="ml-2 h-[26px] cursor-pointer font-bold uppercase"
          color-index="15"
          data-cy="assign-number-toggle"
        >
          <div class="flex items-center space-x-2">
            <div class="truncate text-left">
              <span
                v-if="savedPhoneNumber || resolvedNumber"
                class="text-label"
              >
                {{ prepend }}
                {{ displayNumber }}
              </span>
              <span v-else>
                {{ $t("TWILIO.COMMUNICATE_FROM") }}
              </span>
            </div>
            <icon-base
              :icon="IconArrowUp"
              height="10"
              width="10"
              :class="{
                '-translate-y-1 rotate-180': numbersTooltipVisible,
              }"
            />
          </div>
        </lf-pill>
        <span
          v-if="savedPhoneNumberLocation"
          v-tooltipable
          data-cy="sms-from-number-location"
          class="max-w-60 truncate text-xs text-label"
        >
          {{ savedPhoneNumberLocation }}
        </span>
        <copy-to-clipboard-button
          v-if="savedPhoneNumber?.phone_number"
          class="hidden group-hover:block"
          :value="savedPhoneNumber.phone_number"
          @click.stop
        />
      </div>
    </template>
  </communication-profile-numbers>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { PhoneSelectionType } from "@/enums/communicationLogs";
import { useTwilio } from "@/hooks/communicationProviders";
import { useTwilioStore } from "@/stores/twilio";

import CopyToClipboardButton from "@/components/CopyToClipboardButton.vue";
import CommunicationProfileNumbers from "@/components/communication/CommunicationProfileNumbers.vue";
import IconArrowUp from "@/components/icons/IconArrowUp.vue";

import type { TippyComponent } from "vue-tippy";

const {
  prepend = "",
  placement = "bottom-start",
  resolvedNumber = "",
} = defineProps<{
  prepend?: string;
  placement?: TippyComponent["popperOptions"]["placement"];
  resolvedNumber?: string;
}>();

const { savedPhoneNumber, savedPhoneDisplay } = useTwilio();
const twilioStore = useTwilioStore();

const displayNumber = computed(() => {
  if (
    resolvedNumber &&
    (!savedPhoneNumber.value ||
      savedPhoneDisplay.value === PhoneSelectionType.Auto)
  ) {
    return resolvedNumber;
  }
  return savedPhoneDisplay.value;
});

const savedPhoneNumberLocation = computed(() => {
  const number = displayNumber.value;
  if (!number || number === PhoneSelectionType.Auto) {
    return null;
  }
  const match = twilioStore.userAssignedPhoneNumbers?.find(
    (phone) => phone.phone_number === number
  );
  return match?.location ?? null;
});
</script>
